<template>
  <Transition name="modal-fade">
    <div v-if="visible" class="flow-reorder-modal">
      <div class="flow-reorder-modal__backdrop" @click="$emit('close')"></div>
      <div class="flow-reorder-modal__content">
        <div class="flow-reorder-modal__header">
          <h3>{{ t("flows.branchReorder.title") }}</h3>
          <button class="flow-reorder-modal__close" @click="$emit('close')">
            <i class="far fa-times" />
          </button>
        </div>

        <div class="flow-reorder-modal__body">
          <draggable
            v-if="branches.length > 0"
            v-model="branches"
            item-key="originalIndex"
            class="flow-reorder-modal__list"
            handle=".flow-reorder-modal__drag-handle"
          >
            <template #item="{ element }">
              <div class="flow-reorder-modal__item">
                <!-- Drag Handle -->
                <div class="flow-reorder-modal__drag-handle">
                  <i class="fas fa-grip-vertical" />
                </div>

                <!-- Node Content (Replicating FlowNode style) -->
                <div
                  class="flow-reorder-modal__node"
                  :class="[
                    `flow-reorder-modal__node--${element.kind}`,
                    { 'flow-reorder-modal__node--empty': element.isEmpty }
                  ]"
                >
                  <div class="flow-reorder-modal__node-content">
                    <div class="flow-reorder-modal__color-bar" />
                    <div class="flow-reorder-modal__main-row">
                      <div class="flow-reorder-modal__icon">
                        <i :class="element.icon" />
                      </div>
                      <div class="flow-reorder-modal__info">
                        <div class="flow-reorder-modal__label">
                          {{ element.label }}
                        </div>
                        <div
                          v-if="element.subtitle"
                          class="flow-reorder-modal__subtitle"
                        >
                          {{ element.subtitle }}
                        </div>
                      </div>
                      <div class="flow-reorder-modal__output-badge">
                        {{ t("flows.branchReorder.output") }}
                        {{ element.originalIndex }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </draggable>
        </div>

        <div class="flow-reorder-modal__footer">
          <Button
            :label="t('flows.branchReorder.cancel')"
            class="p-button-text"
            @click="$emit('close')"
          />
          <Button
            :label="t('flows.branchReorder.save')"
            class="p-button-success"
            @click="handleSave"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import draggable from 'vuedraggable';
import Button from 'primevue/button';
import type { FlowStructureDto, FlowStepDto } from '@/types/flow-structure';
import { getOperatorById } from '@/components/flows/operators/registry';
import { parseOperatorParameters } from '@/helpers/flowParameters';

interface BranchItem {
  originalIndex: number; // 1-based index
  label: string;
  subtitle: string;
  icon: string;
  kind: string;
  isEmpty: boolean;
  childId?: string;
}

export default defineComponent({
  name: 'FlowBranchReorderModal',
  components: {
    draggable,
    Button,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    stepId: {
      type: String,
      default: null,
    },
    flow: {
      type: Object as PropType<FlowStructureDto | null>,
      default: null,
    },
  },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const branches = ref<BranchItem[]>([]);

    const initBranches = () => {
      if (!props.flow || !props.stepId) return;

      const step = props.flow.steps.find((s) => s.id === props.stepId);
      if (!step) return;

      const operator = getOperatorById(step.type);
      if (!operator) return;

      // Determine total outputs
      let totalOutputs = 1;
      const outputsDef = operator.outputs ?? 1;
      if (typeof outputsDef === 'function') {
        const params = parseOperatorParameters(step.parameters);
        totalOutputs = outputsDef(params);
      } else {
        totalOutputs = outputsDef;
      }

      const items: BranchItem[] = [];

      for (let i = 1; i <= totalOutputs; i += 1) {
        // Find connected child
        const connection = props.flow.connections.find(
          (c) => c.parentId === props.stepId && c.parentOutput === i
        );

        if (connection) {
          const childStep = props.flow.steps.find((s) => s.id === connection.childId);
          if (childStep) {
            const childOperator = getOperatorById(childStep.type);

            // Replicate FlowNode label logic
            let labelText = childStep.type;
            if (childOperator) {
              if (childOperator.getDisplayLabel) {
                 const generated = childOperator.getDisplayLabel(parseOperatorParameters(childStep.parameters), t);
                 if (generated) labelText = generated;
                 else if (childOperator.label) labelText = t(childOperator.label);
              } else if (childOperator.label) {
                 labelText = t(childOperator.label);
              }
            }

            items.push({
              originalIndex: i,
              label: labelText,
              subtitle: childOperator?.getSubtitle ? (childOperator.getSubtitle(parseOperatorParameters(childStep.parameters), t) || '') : '',
              icon: childOperator?.icon ? `far ${childOperator.icon}` : 'far fa-square', // Default icon
              kind: childOperator?.kind || 'action',
              isEmpty: false,
              childId: childStep.id,
            });
          } else {
             // Connected but child not found (shouldn't happen)
             items.push(createEmptyBranch(i));
          }
        } else {
          items.push(createEmptyBranch(i));
        }
      }

      branches.value = items;
    };

    const createEmptyBranch = (index: number): BranchItem => ({
      originalIndex: index,
      label: t('flows.branchReorder.emptyOperator'),
      subtitle: '',
      icon: 'far fa-dashed-square',
      kind: 'empty',
      isEmpty: true,
    });

    watch(
      () => [props.visible, props.stepId, props.flow],
      () => {
        if (props.visible) {
          initBranches();
        }
      },
      { immediate: true }
    );

    const handleSave = () => {
      // Emit the new order of indices
      const newOrder = branches.value.map((b) => b.originalIndex);
      emit('save', { stepId: props.stepId, newOrder });
      emit('close');
    };

    return {
      t,
      branches,
      handleSave,
    };
  },
});
</script>

<style lang="scss" scoped>
.flow-reorder-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;

  &__backdrop {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
  }

  &__content {
    position: relative;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
    width: 500px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;

    h3 {
      font-size: 1.125rem;
      font-weight: 600;
      color: #111827;
      margin: 0;
    }
  }

  &__close {
    background: transparent;
    border: none;
    color: #6b7280;
    cursor: pointer;
    font-size: 1.25rem;
    padding: 4px;

    &:hover {
      color: #111827;
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background-color: #f9fafb;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__drag-handle {
    color: #9ca3af;
    cursor: grab;
    padding: 8px;

    &:active {
      cursor: grabbing;
    }

    &:hover {
      color: #4b5563;
    }
  }

  // Node visualization (replicating FlowNode colors)
  &__node {
    flex: 1;
    position: relative;

    &-content {
      position: relative;
      display: flex;
      flex-direction: column;
      background: #fff;
      border-radius: 0.5rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e7eb;
      min-height: 60px;
      overflow: hidden;
    }
  }

  // Exact Copy of Color Schemes from FlowNode.vue
  &__node--trigger {
    .flow-reorder-modal__color-bar {
      background: #f59e0b;
    }
    .flow-reorder-modal__icon {
      background: #fef3c7;
      color: #d97706;
    }
  }

  &__node--action {
    .flow-reorder-modal__color-bar {
      background: #22c55e;
    }
    .flow-reorder-modal__icon {
      background: #dcfce7;
      color: #16a34a;
    }
  }

  &__node--filter {
    .flow-reorder-modal__color-bar {
      background: #3b82f6;
    }
    .flow-reorder-modal__icon {
      background: #dbeafe;
      color: #2563eb;
    }
  }

  // Empty State (Gray)
  &__node--empty {
    .flow-reorder-modal__color-bar {
      background: #e5e7eb;
    }
    .flow-reorder-modal__icon {
      color: #9ca3af;
    }
    .flow-reorder-modal__label {
      color: #9ca3af;
    }
  }

  &__color-bar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-radius: 0.5rem 0 0 0.5rem;
  }

  &__main-row {
    display: flex;
    align-items: center;
    height: 60px;
    padding-right: 16px;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin: 0.75rem 0.625rem 0.75rem calc(4px + 1rem);
    border-radius: 0.5rem;
    flex-shrink: 0;

    i {
      font-size: 1rem;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__label {
    font-weight: 600;
    font-size: 0.8125rem;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__subtitle {
    color: #6b7280;
    font-size: 0.6875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 0.125rem;
  }

  &__output-badge {
    margin-left: auto;
    font-size: 0.75rem;
    color: #6b7280;
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 20px;
    border-top: 1px solid #e5e7eb;
    background: #fff;

    :deep(.p-button-text) {
      color: #6b7280 !important;
      border: 1px solid #d1d5db !important;
      background: white !important;
      padding: 0.625rem 1.25rem;

      &:hover {
        background: #f9fafb !important;
        border-color: #9ca3af !important;
        color: #374151 !important;
      }
    }

    :deep(.p-button-success) {
      background: #94c840 !important;
      color: white !important;
      border: none !important;
      font-weight: 500;
      padding: 0.625rem 1.25rem;

      &:hover {
        background: #7fb835 !important;
      }
    }
  }
}

// Transitions
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
